/**
 * Referral API Service
 * Handles all API calls for the gated referral system
 */

import { getBackendUrl } from '../utils/backendUrl';

const BACKEND_URL = getBackendUrl();

export interface ReferralAccessCheck {
  canAccess: boolean;
  reason: 'existing_user' | 'has_referral' | 'needs_referral_code' | 'whitelisted' | 'dev_bypass' | 'error';
  needsReferral: boolean;
}

export interface ReferralCodeData {
  code: string;
  isNew: boolean;
  shareUrl: string;
}

export interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  totalVolume: number;
  referralDetails: Array<{
    wallet_address: string;
    referred_at: string;
    first_bet_at: string | null;
    session_count: number;
    volume: number;
    is_active: boolean;
  }>;
}

export interface ReferralEarnings {
  totalEarned: number;
  totalClaimed: number;
  claimableNow: number;
  lastClaimAt: string | null;
  lastClaimAmount: number | null;
  lastClaimTx: string | null;
  canClaimNow: boolean;
  nextClaimAvailable: string | null;
}

export interface ReferralEarningsInfo {
  stats: {
    totalReferrals: number;
    activeReferrals: number;
    totalVolume: number;
  };
  earnings: ReferralEarnings;
}

/**
 * Check if wallet can access the site
 * @param walletAddress - User's wallet address
 * @returns Access status
 */
export async function checkAccess(walletAddress: string): Promise<{
  success: boolean;
  data?: ReferralAccessCheck;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/check-access/${walletAddress}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error checking access:', error);
    return {
      success: false,
      error: 'Failed to check access'
    };
  }
}

/**
 * Validate a referral code
 * @param code - Referral code to validate
 * @returns Validation result
 */
export async function validateCode(code: string): Promise<{
  success: boolean;
  data?: { valid: boolean; referrer?: { wallet: string } };
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/validate/${code}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error validating code:', error);
    return {
      success: false,
      error: 'Failed to validate code'
    };
  }
}

/**
 * Apply referral code to wallet
 * @param walletAddress - User's wallet address
 * @param code - Referral code to apply
 * @returns Application result
 */
export async function applyCode(walletAddress: string, code: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        walletAddress,
        code: code.toUpperCase()
      })
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error applying code:', error);
    return {
      success: false,
      error: 'Failed to apply referral code'
    };
  }
}

/**
 * Get or create user's referral code (lazy generation)
 * @param walletAddress - User's wallet address
 * @returns Referral code data
 */
export async function getMyCode(walletAddress: string): Promise<{
  success: boolean;
  data?: ReferralCodeData;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/my-code/${walletAddress}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error getting code:', error);
    return {
      success: false,
      error: 'Failed to get referral code'
    };
  }
}

/**
 * Get user's referral statistics
 * @param walletAddress - User's wallet address
 * @returns Referral stats
 */
export async function getStats(walletAddress: string): Promise<{
  success: boolean;
  data?: ReferralStats;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/stats/${walletAddress}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error getting stats:', error);
    return {
      success: false,
      error: 'Failed to get referral stats'
    };
  }
}

/**
 * Get referral leaderboard
 * @param limit - Number of top referrers to return
 * @returns Leaderboard data
 */
export async function getLeaderboard(limit: number = 10): Promise<{
  success: boolean;
  data?: { leaderboard: Array<{ wallet_address: string; referral_code: string; referral_count: number }> };
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/leaderboard?limit=${limit}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error getting leaderboard:', error);
    return {
      success: false,
      error: 'Failed to get leaderboard'
    };
  }
}

/**
 * Get earnings info for a wallet (stats + claimable amount)
 * @param walletAddress - User's wallet address
 * @returns Earnings info
 */
export async function getEarnings(walletAddress: string): Promise<{
  success: boolean;
  data?: ReferralEarningsInfo;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/earnings/${walletAddress}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error getting earnings:', error);
    return {
      success: false,
      error: 'Failed to get earnings'
    };
  }
}

/**
 * Claim referral earnings
 * @param walletAddress - User's wallet address
 * @returns Claim result with transaction signature
 */
export async function claimEarnings(walletAddress: string): Promise<{
  success: boolean;
  data?: {
    claimedAmount: number;
    transactionSignature: string;
    newTotalClaimed: number;
  };
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/referral/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ walletAddress })
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ REFERRAL API: Error claiming earnings:', error);
    return {
      success: false,
      error: 'Failed to claim earnings'
    };
  }
}

const referralApiService = {
  checkAccess,
  validateCode,
  applyCode,
  getMyCode,
  getStats,
  getLeaderboard,
  getEarnings,
  claimEarnings
};

export default referralApiService;

